import React, { useState, useEffect } from 'react';
import API_BASE from '../config';

function AdminConfig({ accessCode }) {
  const [config, setConfig] = useState({ menus: [], relations: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Edit states
  const [editingMenu, setEditingMenu] = useState(null);
  const [editingRelation, setEditingRelation] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editMenuName, setEditMenuName] = useState('');
  const [editMenuPrice, setEditMenuPrice] = useState(0);
  
  // Add states
  const [newMenuName, setNewMenuName] = useState('');
  const [newMenuPrice, setNewMenuPrice] = useState(0);
  const [newRelation, setNewRelation] = useState('');
  
  // Delete states
  const [deletingMenu, setDeletingMenu] = useState(null);
  const [deletingRelation, setDeletingRelation] = useState(null);
  const [replacement, setReplacement] = useState('');

  // Deadline state
  const [deadlineInput, setDeadlineInput] = useState('');

  // Event info state (for liberatoria)
  const [eventDate, setEventDate] = useState('');
  const [eventLocation, setEventLocation] = useState('');

  // Representative lock date state
  const [repLockInput, setRepLockInput] = useState('');

  // CSV upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/config`);
      const data = await response.json();
      setConfig(data);
      if (data.registrationDeadline) {
        setDeadlineInput(data.registrationDeadline.substring(0, 10));
      }
      if (data.eventDate) setEventDate(data.eventDate);
      if (data.eventLocation) setEventLocation(data.eventLocation);
      if (data.representativeLockDate) setRepLockInput(data.representativeLockDate.substring(0, 10));
      setLoading(false);
    } catch (err) {
      setError('Errore nel/nella caricamento configurazione');
      setLoading(false);
    }
  };

  const handleAddMenu = async () => {
    if (!newMenuName.trim()) return;
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_BASE}/config/menus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ name: newMenuName, price: Number(newMenuPrice) })
      });
      
      if (response.ok) {
        setSuccess('Menù aggiunto con successo');
        setNewMenuName('');
        setNewMenuPrice(0);
        fetchConfig();
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Errore nel/nella aggiunta menù');
    }
  };

  const handleAddRelation = async () => {
    if (!newRelation.trim()) return;
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_BASE}/config/relations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ value: newRelation })
      });
      
      if (response.ok) {
        setSuccess('Relazione aggiunta con successo');
        setNewRelation('');
        fetchConfig();
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Errore nel/nella aggiunta relazione');
    }
  };

  const handleEditMenu = async (oldName) => {
    if (!editMenuName.trim()) return;
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_BASE}/config/menus/${encodeURIComponent(oldName)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ newName: editMenuName, newPrice: Number(editMenuPrice) })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess(`Menù aggiornato. ${data.updatedRegistrations} registrazioni interessate.`);
        setEditingMenu(null);
        setEditMenuName('');
        setEditMenuPrice(0);
        fetchConfig();
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Errore nel/nella aggiornamento menù');
    }
  };

  const handleEditRelation = async (oldValue) => {
    if (!editValue.trim()) return;
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_BASE}/config/relations/${encodeURIComponent(oldValue)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ newValue: editValue })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess(`Relazione aggiornata. ${data.updatedRegistrations} registrazioni interessate.`);
        setEditingRelation(null);
        setEditValue('');
        fetchConfig();
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Errore nel/nella aggiornamento relazione');
    }
  };

  const handleDeleteMenu = async (menuName) => {
    if (!replacement) {
      setError('Selezionare un valore sostitutivo');
      return;
    }
    
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_BASE}/config/menus/${encodeURIComponent(menuName)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ replacement })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess(`Menù eliminato. ${data.updatedRegistrations} registrazioni aggiornate con il sostituto.`);
        setDeletingMenu(null);
        setReplacement('');
        fetchConfig();
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Errore nel/nella eliminazione menù');
    }
  };

  const handleDeleteRelation = async (value) => {
    if (!replacement) {
      setError('Selezionare un valore sostitutivo');
      return;
    }
    
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_BASE}/config/relations/${encodeURIComponent(value)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ replacement })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess(`Relazione eliminata. ${data.updatedRegistrations} registrazioni aggiornate con il sostituto.`);
        setDeletingRelation(null);
        setReplacement('');
        fetchConfig();
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Errore nel/nella eliminazione relazione');
    }
  };

  const handleUploadCsv = async () => {
    if (!csvFile) return;
    setCsvUploading(true);
    setCsvResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await fetch(`${API_BASE}/students/upload`, {
        method: 'POST',
        headers: { 'x-access-code': accessCode },
        body: formData
      });
      
      const data = await response.json();
      if (response.ok) {
        setCsvResult(data);
        setCsvFile(null);
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
      } else {
        setCsvResult({ error: data.error || 'Errore nel caricamento' });
      }
    } catch (err) {
      setCsvResult({ error: 'Errore nel caricamento del file' });
    }
    setCsvUploading(false);
  };

  const handleSaveDeadline = async () => {
    setError('');
    setSuccess('');
    try {
      const deadline = deadlineInput ? new Date(deadlineInput + 'T23:59:59.000Z').toISOString() : null;
      const response = await fetch(`${API_BASE}/config/deadline`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ registrationDeadline: deadline })
      });
      if (response.ok) {
        setSuccess('Scadenza aggiornata con successo');
        fetchConfig();
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Errore nell\'aggiornamento della scadenza');
    }
  };

  const handleRemoveDeadline = async () => {
    setDeadlineInput('');
    try {
      await fetch(`${API_BASE}/config/deadline`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ registrationDeadline: null })
      });
      setSuccess('Scadenza rimossa');
      fetchConfig();
    } catch (err) {
      setError('Errore nella rimozione della scadenza');
    }
  };

  const handleSaveEvent = async () => {
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE}/config/event`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ eventDate, eventLocation })
      });
      if (response.ok) {
        setSuccess('Informazioni evento aggiornate');
        fetchConfig();
      } else {
        setError('Errore nell\'aggiornamento');
      }
    } catch (err) {
      setError('Errore nell\'aggiornamento delle informazioni evento');
    }
  };

  const handleSaveRepLock = async () => {
    setError('');
    setSuccess('');
    try {
      const lockDate = repLockInput ? new Date(repLockInput + 'T23:59:59.000Z').toISOString() : null;
      const response = await fetch(`${API_BASE}/config/representative-lock`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ representativeLockDate: lockDate })
      });
      if (response.ok) {
        setSuccess('Data blocco rappresentanti aggiornata');
        fetchConfig();
      } else {
        setError('Errore nell\'aggiornamento');
      }
    } catch (err) {
      setError('Errore nell\'aggiornamento della data di blocco');
    }
  };

  const handleRemoveRepLock = async () => {
    setRepLockInput('');
    try {
      await fetch(`${API_BASE}/config/representative-lock`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ representativeLockDate: null })
      });
      setSuccess('Data blocco rappresentanti rimossa');
      fetchConfig();
    } catch (err) {
      setError('Errore nella rimozione della data di blocco');
    }
  };

  if (loading) return <div><p>Caricamento...</p></div>;

  return (
    <div>
      <h2>Configurazione del Sistema</h2>
      
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="config-section">
        <h3>📅 Scadenza Registrazioni</h3>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
          Dopo questa data, i genitori non potranno più registrarsi o modificare le registrazioni. Gli admin possono comunque operare.
        </p>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Data di scadenza</label>
            <input type="date" value={deadlineInput} onChange={(e) => setDeadlineInput(e.target.value)} />
          </div>
          <button className="btn-primary" onClick={handleSaveDeadline} style={{ height: '40px' }}>Salva</button>
          {config.registrationDeadline && (
            <button className="btn-danger" onClick={handleRemoveDeadline} style={{ height: '40px' }}>Rimuovi</button>
          )}
        </div>
        {config.registrationDeadline && (
          <p style={{ marginTop: '8px', fontSize: '13px' }}>
            Scadenza attuale: <strong>{config.registrationDeadline.substring(0, 10).split('-').reverse().join('/')}</strong>
            {new Date() > new Date(config.registrationDeadline) ? <span style={{ color: '#e74c3c' }}> — Registrazioni chiuse</span> : <span style={{ color: '#27ae60' }}> — Registrazioni aperte</span>}
          </p>
        )}
      </div>

      <div className="config-section">
        <h3>🎪 Informazioni Evento (Liberatoria)</h3>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
          Data e luogo dell'evento, utilizzati nella liberatoria.
        </p>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: '200px' }}>
            <label>Data evento</label>
            <input type="text" value={eventDate} onChange={(e) => setEventDate(e.target.value)} placeholder="es. 5 giugno 2026" />
          </div>
          <div className="form-group" style={{ marginBottom: 0, minWidth: '150px' }}>
            <label>Luogo</label>
            <input type="text" value={eventLocation} onChange={(e) => setEventLocation(e.target.value)} placeholder="es. Arese" />
          </div>
          <button className="btn-primary" onClick={handleSaveEvent} style={{ height: '40px' }}>Salva</button>
        </div>
      </div>

      <div className="config-section">
        <h3>🔒 Blocco Rappresentanti</h3>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
          Dopo questa data, i rappresentanti avranno accesso in sola lettura al dashboard (nessuna modifica, creazione o eliminazione). Gli organizzatori non sono interessati.
        </p>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Data di blocco</label>
            <input type="date" value={repLockInput} onChange={(e) => setRepLockInput(e.target.value)} />
          </div>
          <button className="btn-primary" onClick={handleSaveRepLock} style={{ height: '40px' }}>Salva</button>
          {config.representativeLockDate && (
            <button className="btn-danger" onClick={handleRemoveRepLock} style={{ height: '40px' }}>Rimuovi</button>
          )}
        </div>
        {config.representativeLockDate && (
          <p style={{ marginTop: '8px', fontSize: '13px' }}>
            Blocco attivo dal: <strong>{config.representativeLockDate.substring(0, 10).split('-').reverse().join('/')}</strong>
            {new Date() > new Date(config.representativeLockDate) ? <span style={{ color: '#e74c3c' }}> — Rappresentanti bloccati</span> : <span style={{ color: '#27ae60' }}> — Non ancora attivo</span>}
          </p>
        )}
      </div>

      <div className="config-section">
        <h3>📋 Lista Studenti</h3>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
          Caricare un file CSV con le colonne: <strong>nome</strong> (o firstName), <strong>cognome</strong> (o lastName), <strong>classe</strong> (o class). 
          Separatore: virgola o punto e virgola. Gli studenti esistenti (stesso nome + cognome + classe) verranno aggiornati.
        </p>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>File CSV</label>
            <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0])} />
          </div>
          <button className="btn-primary" onClick={handleUploadCsv} disabled={!csvFile || csvUploading} style={{ height: '40px' }}>
            {csvUploading ? 'Caricamento...' : 'Carica'}
          </button>
        </div>
        {csvResult && (
          <div className={csvResult.error ? 'error' : 'success'} style={{ marginTop: '10px' }}>
            {csvResult.error || csvResult.message}
            {csvResult.errors && csvResult.errors.length > 0 && (
              <ul style={{ marginTop: '5px', fontSize: '12px' }}>
                {csvResult.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="config-section">
        <h3>Opzioni Menù</h3>
        
        <div className="config-list">
          {config.menus.map((menu, index) => (
            <div key={index} className="config-item">
              {editingMenu === menu.name ? (
                <div className="config-edit">
                  <input
                    type="text"
                    value={editMenuName}
                    onChange={(e) => setEditMenuName(e.target.value)}
                    placeholder="Nome menù"
                  />
                  <input
                    type="number"
                    value={editMenuPrice}
                    onChange={(e) => setEditMenuPrice(e.target.value)}
                    placeholder="Prezzo"
                    style={{ width: '80px' }}
                  />
                  <button className="btn-primary" onClick={() => handleEditMenu(menu.name)}>
                    Salva
                  </button>
                  <button className="btn-secondary" onClick={() => { setEditingMenu(null); setEditMenuName(''); setEditMenuPrice(0); }}>
                    Annulla
                  </button>
                </div>
              ) : deletingMenu === menu.name ? (
                <div className="config-delete">
                  <span>{menu.name}</span>
                  <select value={replacement} onChange={(e) => setReplacement(e.target.value)}>
                    <option value="">Seleziona sostituto</option>
                    {config.menus.filter(m => m.name !== menu.name).map(m => (
                      <option key={m.name} value={m.name}>{m.name}</option>
                    ))}
                  </select>
                  <button className="btn-danger" onClick={() => handleDeleteMenu(menu.name)}>
                    Conferma Eliminazione
                  </button>
                  <button className="btn-secondary" onClick={() => { setDeletingMenu(null); setReplacement(''); }}>
                    Annulla
                  </button>
                </div>
              ) : (
                <div className="config-display">
                  <span>{menu.name} - €{menu.price}</span>
                  <div>
                    <button className="btn-secondary" onClick={() => { setEditingMenu(menu.name); setEditMenuName(menu.name); setEditMenuPrice(menu.price); }}>
                      Modifica
                    </button>
                    <button className="btn-danger" onClick={() => setDeletingMenu(menu.name)}>
                      Elimina
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="config-add">
          <input
            type="text"
            value={newMenuName}
            onChange={(e) => setNewMenuName(e.target.value)}
            placeholder="Nome menù"
          />
          <input
            type="number"
            value={newMenuPrice}
            onChange={(e) => setNewMenuPrice(e.target.value)}
            placeholder="Prezzo"
            style={{ width: '80px' }}
          />
          <button className="btn-primary" onClick={handleAddMenu}>
            Aggiungi Menù
          </button>
        </div>
      </div>

      <div className="config-section">
        <h3>Tipi di Relazione</h3>
        
        <div className="config-list">
          {config.relations.map((relation, index) => (
            <div key={index} className="config-item">
              {editingRelation === relation ? (
                <div className="config-edit">
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder="Nuovo nome relazione"
                  />
                  <button className="btn-primary" onClick={() => handleEditRelation(relation)}>
                    Salva
                  </button>
                  <button className="btn-secondary" onClick={() => { setEditingRelation(null); setEditValue(''); }}>
                    Annulla
                  </button>
                </div>
              ) : deletingRelation === relation ? (
                <div className="config-delete">
                  <span>{relation}</span>
                  <select value={replacement} onChange={(e) => setReplacement(e.target.value)}>
                    <option value="">Seleziona sostituto</option>
                    {config.relations.filter(r => r !== relation).map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <button className="btn-danger" onClick={() => handleDeleteRelation(relation)}>
                    Conferma Eliminazione
                  </button>
                  <button className="btn-secondary" onClick={() => { setDeletingRelation(null); setReplacement(''); }}>
                    Annulla
                  </button>
                </div>
              ) : (
                <div className="config-display">
                  <span>{relation}</span>
                  <div>
                    <button className="btn-secondary" onClick={() => { setEditingRelation(relation); setEditValue(relation); }}>
                      Modifica
                    </button>
                    <button className="btn-danger" onClick={() => setDeletingRelation(relation)}>
                      Elimina
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="config-add">
          <input
            type="text"
            value={newRelation}
            onChange={(e) => setNewRelation(e.target.value)}
            placeholder="Nuovo tipo di relazione"
          />
          <button className="btn-primary" onClick={handleAddRelation}>
            Aggiungi Relazione
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminConfig;
